<template>
  <div class="w-full px-6 py-4">
    <div class="mb-5 flex items-center">
      <o-icon icon="marketing" :size="20" class="mr-3 opacity-75" />
      <div>
        <p class="leading-none mb-1">
          {{ $t('components.customer.edit.marketing.title') }}
        </p>
        <p class="leading-none text-sm opacity-60">
          {{ $t('components.customer.edit.marketing.info') }}
        </p>
      </div>
    </div>

    <div>
      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full mb-4" :label="$t('customer.sendNotifications')">
          <select-field
            v-model="_sendNotifications"
            :options="customerNotificationOptions"
          />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full mb-4" :label="$t('general.allowOnlineBooking')">
          <toggle-field v-model="_allowOnlineBooking" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full mb-4" :label="$t('components.customer.edit.marketing.massEmailUnsubscribed')">
          <toggle-field v-model="_massEmailUnsubscribed" />
        </o-field-group>
      </div>

      <div class="flex space-x-4 mb-4">
        <o-field-group class="w-full mb-4" :label="$t('components.customer.edit.marketing.massSMSUnsubscribed')">
          <toggle-field v-model="_massSMSUnsubscribed" />
        </o-field-group>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type Customer,
  CustomerNotifications,
  CustomerNotificationsLabels,
  CustomerMassEmail,
  CustomerMassSMS,
} from '~/_api/models/customer'

import {
  SelectField,
  ToggleField,
} from '@/components/fields'

interface Props {
  customer: Customer
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:customer', value: any): void
}>()

const { $i18n } = useNuxtApp()

const _customer = computed({
  get () {
    return props.customer
  },
  set (value) {
    emit('update:customer', value)
  },
})

const _sendNotifications = computed({
  get () {
    return props.customer.sendNotifications
  },
  set (value) {
    _customer.value.sendNotifications = value
  },
})

const _allowOnlineBooking = computed({
  get () {
    return !!props.customer.allowOnlineBooking
  },
  set (value) {
    _customer.value.allowOnlineBooking = value
  },
})

const _massEmailUnsubscribed = computed({
  get () {
    return props.customer.massEmailUnsubscribed === CustomerMassEmail.unsubscribed
  },
  set (value: any) {
    _customer.value.massEmailUnsubscribed = value ? CustomerMassEmail.unsubscribed : CustomerMassEmail.subscribed
  },
})

const _massSMSUnsubscribed = computed({
  get () {
    return props.customer.massSMSUnsubscribed === CustomerMassSMS.unsubscribed
  },
  set (value: any) {
    _customer.value.massSMSUnsubscribed = value ? CustomerMassSMS.unsubscribed : CustomerMassSMS.subscribed
  },
})

const customerNotificationOptions = computed(() => {
  return Object.values(CustomerNotifications)
    .filter((value): value is CustomerNotifications => typeof value === 'number')
    .map((value) => {
      return {
        label: $i18n.t(CustomerNotificationsLabels[value]),
        value,
      }
    })
})
</script>
